import { Avatar, Box, Button, Link, Typography } from "@mui/material";
import { UserInfo } from "../../types";
import { getUserData } from "../../utils/userDataFormatter";
import { ChatIcon, FollowIcon, VerificationIcon } from "../../assets/icons/MUI/NetworkIcons";
import { useNavigate } from "react-router-dom";
import { colors } from "../../theme/palette";
import { followEntity, followUser } from "../../api/network";
import { useInvalidateMyQuery } from "../../hooks/useInvalidateMyQuery";
import { useState } from "react";
import typography from "../../theme/typography";
import NewChatPopUp from "../NewChatPopUp";
import { useTranslation } from "react-i18next";
import { useDataFormatter } from "../../hooks/useDataFormatter";

const TrendingCard = ({ user }: { user: UserInfo }) => {
  const [openCreateMessage, setOpenCreateMessage] = useState<boolean>(false);

  const navigate = useNavigate();
  const {
    fullName,
    firstTwoChr,
    isVerified,
    media,
    role,
    isFollowing,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
    id,
  } = useDataFormatter(user);
  const invalidateNetworkFollowersList = useInvalidateMyQuery("NetworkList");
  const [isFollowingState, setIsFollowingState] = useState<boolean>(isFollowing || false);

  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",
        alignItems: "flex-start",
        paddingBottom: "17px",
        marginBottom: "17px",
        borderBottom: "1px solid #EAECF0",
      }}
    >
      <Box sx={{ position: "relative" }}>
        {isVerified && (
          <VerificationIcon
            sx={{
              position: "absolute",
              top: "35px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: "10",
            }}
          />
        )}
        <Avatar
          sx={{
            width: "49px",
            height: "49px",
          }}
          src={media}
        >
          {firstTwoChr}
        </Avatar>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "8px", flex: 1 }}>
        <Link
          sx={{
            textDecoration: "none",
            color: colors.Dark1000,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            cursor: "pointer",
            ...typography.body14SemiBold,
            lineHeight: "normal",
          }}
          onClick={() => navigate(profileLink)}
        >
          {fullName}
        </Link>
        <Typography
          component="span"
          variant="body12SemiBold"
          sx={{
            color: colors.Dark1000,
          }}
        >
          {role}
          {playerPositionShort ? ` . (${playerPositionShort})` : ""}
        </Typography>
        <Typography
          component="span"
          variant="body12Regular"
          sx={{
            color: colors.Dark600,
          }}
        >
          {`${mutualFollowersCount} ${t("components.trending.mutualFollowers")}`}
          {friendsCount ? ` · ${t("components.trending.friend")} ${friendsCount}` : ""}
        </Typography>

        {isFollowingState ? (
          <>
            <Button
              variant="secondary-sm"
              startIcon={<ChatIcon />}
              sx={{
                marginTop: "auto",
              }}
              onClick={() => setOpenCreateMessage(true)}
            >
              {t("components.trending.chat")}
            </Button>
            {openCreateMessage && (
              <NewChatPopUp
                open={openCreateMessage}
                handleClose={() => setOpenCreateMessage(false)}
                receiverInfo={user}
              />
            )}
          </>
        ) : (
          <Button
            variant="outlined"
            startIcon={<FollowIcon />}
            onClick={async () => {
              let success = id ? await followUser(id) : false;
              if (success) {
                invalidateNetworkFollowersList();
                setIsFollowingState(true);
              }
            }}
            sx={{
              marginTop: "auto",
            }}
          >
            {t("components.trending.follow")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TrendingCard;
